import { useEffect, useRef, useState } from "react";
import { getDefaultClassNames } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { Button } from "~/components/ui/Button";
import { Calendar } from "~/components/ui/calendar";
import { cn } from "~/lib/utils";
import { useBookingCart as useBookingCartStore } from "~/stores/bookingCartContext";

// Codici categoria (Omnicommerce) che determinano quali disponibilità mostrare
// nel calendario:
// - booking NORMALE e voucher ingresso/upselling → disponibilità INGRESSI
//   (i pacchetti includono già un ingresso, quindi da soli non devono
//   mostrare disponibilità nella booking normale);
// - voucher PACCHETTO → disponibilità PACCHETTI.
const ENTRANCES_CATEGORY = "CAT_ENTRANCES";
const PACKAGES_CATEGORY = "CAT_PACKAGES";

const DaySelect = ({
  onConfirm,
  onChange,
  onCancel,
  guestsCount,
  disableConfirm,
  defaultDate,
  isVoucherBooking = false,
}: {
  onConfirm: (selectedDate: Date, availabilites: any[]) => void;
  onChange: () => void;
  onCancel: () => void;
  guestsCount: { male: number; female: number };
  disableConfirm?: boolean;
  defaultDate?: Date;
  isVoucherBooking?: boolean;
}) => {
  const { t } = useTranslation();
  const [date, setDate] = useState<Date | undefined>(defaultDate);
  const [prices, setPrices] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const availabilityCalledRef = useRef(false);
  const pickerDefaultClassNames = getDefaultClassNames();

  const { getMinExpirationDate, bookingCart } = useBookingCartStore();
  const minExpirationDate = isVoucherBooking ? getMinExpirationDate() : null;

  // C'è almeno un voucher che copre l'ingresso (o un pacchetto che lo
  // include)? In quel caso il prezzo nel calendario non è ciò che l'utente
  // paga davvero, quindi lo nascondiamo. Se invece i voucher attivi sono
  // tutti upselling (OnlyIntegration), l'ingresso si paga e ha senso mostrare
  // il prezzo.
  const hasEntranceCoveringVoucher =
    !!bookingCart?.vouchers?.some(
      (v) => v.voucherData?.OnlyIntegration !== true,
    );

  // Voucher di tipo PACCHETTO (voucherData.Category === "PACKAGE"): in tal caso
  // il calendario deve mostrare la disponibilità dei PACCHETTI, non degli
  // ingressi.
  const hasPackageVoucher =
    !!bookingCart?.vouchers?.some(
      (v) => v.voucherData?.Category === "PACKAGE",
    );

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const maxDate = minExpirationDate
    ? (() => {
        const date = new Date(minExpirationDate);
        return isNaN(date.getTime()) ? undefined : date;
      })()
    : undefined;

  useEffect(() => {
    if (!availabilityCalledRef.current) {
      availabilityCalledRef.current = true;
      const fetchAvailability = async () => {
        setIsLoading(true);
        try {
          const response = await fetch("/api/booking/availability");
          const data: any = await response.json();

          if (data?.availabilities) {
            const newPrices: any[] = [];
            data.availabilities.forEach((availability: any) => {
              newPrices.push(availability);
            });
            setPrices(newPrices.filter((price: any) => !!price.minprice));
          }
        } catch (error) {
          console.error("ERROR FETCHING AVAILABILITY: ", error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchAvailability();
    }
  }, []);

  return (
    <div
      id="step-day-select"
      className="w-full max-w-3xl mx-auto flex flex-col items-center px-4 lg:px-8 py-8"
    >
      <h3 className="font-bold text-xl text-center">
        {t("Giorno della prenotazione")}
      </h3>
      <p className="text-center mt-2">
        {t("Seleziona il giorno della prenotazione")}
      </p>
      <div
        className={cn(
          "bg-white shadow-md rounded-3xl flex lg:px-4 w-full lg:w-auto pb-2 flex-col items-center mt-4 mx-auto overflow-hidden",
          {
            "animate-pulse": isLoading,
          }
        )}
      >
        <Calendar
          disabled={{
            before: today,
            after: maxDate,
          }}
          buttonVariant={"secondary"}
          onSelect={(date) => {
            if (date) {
              onChange();
              setDate(date);
            }
          }}
          timeZone="UTC"
          mode="single"
          selected={date}
          className="mt-4"
          components={{
            DayButton: ({ day, className, children, ...dayProps }) => {
              const formattedDate = day.date.toISOString().split("T")[0];
              const rowsForDate = prices
                ? prices.filter((p: any) => p.date === formattedDate)
                : [];
              const hasGuestsAvailability = (p: any) =>
                guestsCount.male + guestsCount.female <=
                  p.availability_general &&
                guestsCount.male <= p.availability_man &&
                guestsCount.female <= p.availability_woman;
              const bookableRows = rowsForDate.filter(hasGuestsAvailability);
              // Categoria di disponibilità da mostrare:
              // - voucher PACCHETTO → pacchetti;
              // - booking normale e voucher ingresso/upselling → ingressi.
              // (Era il bug: un gate fisso sugli ingressi azzerava la
              // disponibilità dei voucher pacchetto.)
              const targetCategory =
                isVoucherBooking && hasPackageVoucher
                  ? PACKAGES_CATEGORY
                  : ENTRANCES_CATEGORY;
              const isDayBookable = bookableRows.some(
                (p: any) => p.category === targetCategory
              );
              // Prezzo minimo reale del giorno: il minimo tra le categorie
              // prenotabili (se un pacchetto disponibile costa meno
              // dell'ingresso, mostriamo il prezzo del pacchetto).
              const bookablePrices = isDayBookable
                ? bookableRows.map((p: any) => p.minprice).filter((m: any) => m > 0)
                : [];
              const minPriceForDate =
                bookablePrices.length > 0
                  ? Math.min(...bookablePrices)
                  : undefined;
              return (
                <Button
                  variant="ghost"
                  size="icon"
                  data-day={day.date.toLocaleDateString()}
                  className={cn(
                    "group flex aspect-square h-auto w-full min-w-[--cell-size] flex-col gap-1 font-normal leading-none rounded-full !hover:[&>span]:text-white",
                    pickerDefaultClassNames.day,
                    className
                  )}
                  {...dayProps}
                  disabled={day.date < today || !isDayBookable}
                >
                  <div className="flex w-10 lg:w-12 h-full flex-col items-center justify-center">
                    <span className="text-center lg:text-lg">
                      {day.date.getDate()}
                    </span>
                    {/* Nascondiamo il prezzo solo se l'utente ha voucher che
                        coprono l'ingresso: in quel caso il `minprice` della
                        cache è quello pieno della categoria (pre-voucher) e
                        confonderebbe. Se invece i voucher sono solo upselling,
                        l'ingresso si paga davvero e il prezzo va mostrato. */}
                    {!hasEntranceCoveringVoucher &&
                      minPriceForDate !== undefined &&
                      minPriceForDate > 0 &&
                      day.date >=
                        new Date(
                          new Date().setDate(new Date().getDate() - 1)
                        ) && (
                        <span
                          className={cn("lg:text-md text-center", {
                            "text-secondary-foreground":
                              day.date.toDateString() === date?.toDateString(),
                            "text-green-600":
                              day.date.toDateString() !== date?.toDateString(),
                          })}
                        >
                          €{minPriceForDate}
                        </span>
                      )}
                  </div>
                </Button>
              );
            },
          }}
        />
      </div>

      <div className="w-full flex flex-row items-center justify-center space-x-2 mt-4">
        <Button
          variant={"muted"}
          onClick={() => {
            onCancel();
          }}
        >
          <span>{t("Indietro")}</span>
        </Button>

        <Button
          disabled={disableConfirm || !date || !prices || prices.length === 0}
          variant={"secondary"}
          className=" self-start"
          onClick={() => {
            const priceForDate = prices.filter(
              (p: any) => p.date === date?.toISOString().split("T")[0]
            );
            if (date && priceForDate.length > 0) {
              onConfirm(date, priceForDate);
            }
          }}
        >
          <span>{t("Conferma")}</span>
        </Button>
      </div>
    </div>
  );
};

export default DaySelect;
